import React, { forwardRef, useEffect, useImperativeHandle, useRef, useState } from 'react'
import { FilterChip } from './FilterChip'

export interface EmailTagInputState {
  inputValue: string
  trimmedInput: string
  hasValidPendingEmail: boolean
}

export interface EmailTagInputProps {
  emails: string[]
  onEmailsChange: (emails: string[]) => void
  onInputStateChange?: (state: EmailTagInputState) => void
  onSubmit?: () => void
  placeholder?: string
  className?: string
}

export interface EmailTagInputRef {
  focus: () => void
  getPendingValidEmail: () => string | null
  commitPendingEmail: () => string | null
}

export const EmailTagInput = forwardRef<EmailTagInputRef, EmailTagInputProps>(
  (
    {
      emails,
      onEmailsChange,
      onInputStateChange,
      onSubmit,
      placeholder = 'Enter user email',
      className = ''
    },
    ref
  ) => {
    const inputRef = useRef<HTMLInputElement>(null)
    const [inputValue, setInputValue] = useState('')

    const getPendingValidEmail = () => {
      const trimmedEmail = inputValue.trim().replace(/,/g, '')

      if (!trimmedEmail || !isValidEmail(trimmedEmail) || emails.includes(trimmedEmail)) {
        return null
      }

      return trimmedEmail
    }

    useImperativeHandle(ref, () => ({
      focus: () => inputRef.current?.focus(),
      getPendingValidEmail,
      commitPendingEmail: () => {
        const pendingEmail = getPendingValidEmail()

        if (pendingEmail) {
          onEmailsChange([...emails, pendingEmail])
          setInputValue('')
        }

        return pendingEmail
      }
    }))

    const isValidEmail = (email: string) => /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(email)

    useEffect(() => {
      const trimmedInput = inputValue.trim().replace(/,/g, '')

      onInputStateChange?.({
        inputValue,
        trimmedInput,
        hasValidPendingEmail:
          !!trimmedInput && isValidEmail(trimmedInput) && !emails.includes(trimmedInput)
      })
    }, [emails, inputValue, onInputStateChange])

    const addEmail = (email: string) => {
      const trimmedEmail = email.trim().replace(/,/g, '')
      if (trimmedEmail && isValidEmail(trimmedEmail) && !emails.includes(trimmedEmail)) {
        onEmailsChange([...emails, trimmedEmail])
      }
      setInputValue('')
    }

    const removeEmail = (index: number) => {
      onEmailsChange(emails.filter((_, i) => i !== index))
    }

    const handleKeyDown = (e: React.KeyboardEvent<HTMLInputElement>) => {
      if (e.key === 'Enter' || e.key === ',') {
        e.preventDefault()
        const trimmed = inputValue.trim().replace(/,/g, '')
        if (trimmed) {
          addEmail(inputValue)
        } else if (e.key === 'Enter' && emails.length > 0 && onSubmit) {
          onSubmit()
        }
      } else if (e.key === 'Backspace' && !inputValue && emails.length > 0) {
        onEmailsChange(emails.slice(0, -1))
      }
    }

    const handleBlur = () => {
      if (inputValue.trim()) {
        addEmail(inputValue)
      }
    }

    return (
      <div
        className={`flex items-center gap-2 p-2 min-h-[44px] flex-1 border border-tokens-neutral-border-1 rounded-xl bg-white cursor-text focus-within:border-tokens-primary-2 transition-all overflow-x-auto ${className}`}
        onClick={() => inputRef.current?.focus()}
      >
        {emails.map((email, index) => (
          <FilterChip
            key={index}
            label={email}
            onRemove={() => removeEmail(index)}
            className='flex-shrink-0'
          />
        ))}
        <input
          ref={inputRef}
          type='email'
          value={inputValue}
          onChange={e => setInputValue(e.target.value)}
          onKeyDown={handleKeyDown}
          onBlur={handleBlur}
          placeholder={emails.length === 0 ? placeholder : ''}
          className='flex-1 min-w-[120px] outline-none text-sm bg-transparent text-tokens-neutral-text-primary placeholder:text-tokens-neutral-text-tertiary'
        />
      </div>
    )
  }
)

EmailTagInput.displayName = 'EmailTagInput'

export default EmailTagInput
